"use client";

import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import type { ListingSearchResult } from "@/app/actions/listing-search";

interface ListingItem {
  id: string;
  listing: ListingSearchResult;
  viewedAt?: Date;
}

interface RightTabsContextType {
  history: ListingItem[];
  liked: ListingItem[];
  activeListingId: string | null;
  addToHistory: (listing: ListingSearchResult) => void;
  toggleLike: (listing: ListingSearchResult) => void;
  isLiked: (listingId: string) => boolean;
  setActiveListing: (id: string | null) => void;
  clearHistory: () => void;
  clearLiked: () => void;
  isExpanded: boolean;
  setIsExpanded: (expanded: boolean) => void;
  // Mobile sheet state for responsive design
  isMobileSheetOpen: boolean;
  setMobileSheetOpen: (open: boolean) => void;
}

const RightTabsContext = createContext<RightTabsContextType | undefined>(undefined);

const HISTORY_KEY = "usland_viewing_history";
const LIKED_KEY = "usland_liked_properties";
const MAX_HISTORY = 10;

export function RightTabsProvider({ children }: { children: React.ReactNode }) {
  const [history, setHistory] = useState<ListingItem[]>([]);
  const [liked, setLiked] = useState<ListingItem[]>([]);
  const [activeListingId, setActiveListingId] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobileSheetOpen, setIsMobileSheetOpen] = useState(false);

  // Load from localStorage on mount
  useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        const savedHistory = localStorage.getItem(HISTORY_KEY);
        const savedLiked = localStorage.getItem(LIKED_KEY);

        if (savedHistory) {
          setHistory(JSON.parse(savedHistory));
        }
        if (savedLiked) {
          setLiked(JSON.parse(savedLiked));
        }
      } catch (error) {
        console.error("Error loading from localStorage:", error);
      }
    }
  }, []);

  // Save to localStorage when history or liked changes
  useEffect(() => {
    if (typeof window !== "undefined" && history.length > 0) {
      localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
    }
  }, [history]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem(LIKED_KEY, JSON.stringify(liked));
    }
  }, [liked]);

  const addToHistory = useCallback((listing: ListingSearchResult) => {
    const listingId = listing.id;

    setHistory((prev) => {
      // Remove if already exists
      const filtered = prev.filter((item) => item.id !== listingId);

      // Add to front with timestamp
      const newItem: ListingItem = {
        id: listingId,
        listing,
        viewedAt: new Date(),
      };

      // Keep only last 10 items
      return [newItem, ...filtered].slice(0, MAX_HISTORY);
    });

    // Set as active
    setActiveListingId(listingId);
  }, []);

  const toggleLike = useCallback((listing: ListingSearchResult) => {
    const listingId = listing.id;

    setLiked((prev) => {
      const isAlreadyLiked = prev.some((item) => item.id === listingId);

      if (isAlreadyLiked) {
        // Remove from liked
        return prev.filter((item) => item.id !== listingId);
      } else {
        // Add to liked
        const newItem: ListingItem = {
          id: listingId,
          listing,
        };
        return [...prev, newItem];
      }
    });
  }, []);

  const isLiked = useCallback((listingId: string) => {
    return liked.some((item) => item.id === listingId);
  }, [liked]);

  const setActiveListing = useCallback((id: string | null) => {
    setActiveListingId(id);
  }, []);

  const clearHistory = useCallback(() => {
    setHistory([]);
    if (typeof window !== "undefined") {
      localStorage.removeItem(HISTORY_KEY);
    }
  }, []);

  const clearLiked = useCallback(() => {
    setLiked([]);
    if (typeof window !== "undefined") {
      localStorage.removeItem(LIKED_KEY);
    }
  }, []);

  // Mobile sheet control - wrapped in useCallback for stable reference
  const setMobileSheetOpen = useCallback((open: boolean) => {
    setIsMobileSheetOpen(open);
  }, []);

  return (
    <RightTabsContext.Provider
      value={{
        history,
        liked,
        activeListingId,
        addToHistory,
        toggleLike,
        isLiked,
        setActiveListing,
        clearHistory,
        clearLiked,
        isExpanded,
        setIsExpanded,
        isMobileSheetOpen,
        setMobileSheetOpen,
      }}
    >
      {children}
    </RightTabsContext.Provider>
  );
}

export function useRightTabs() {
  const context = useContext(RightTabsContext);
  if (context === undefined) {
    throw new Error("useRightTabs must be used within a RightTabsProvider");
  }
  return context;
}
